import {
  Issue,
  MediaPickerComponent,
  MediaType,
  Message,
  NewMessage,
  PickerMode,
} from '@gripp/shared-logic';
import { ReactNode, forwardRef } from 'react';
import { View } from 'react-native';
import { Layout } from '../../styles';
import { IssueMessages, IssueMessagesRef } from '../issue/issueMessages';
import { MessageThreadComposer } from './MessageThreadComposer';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type MessageThreadProps = {
  /**
   * The Issue whose messages are displayed. IssueMessages fetches its own
   * messages via the issue.id so we pass the full domain object unchanged.
   */
  issue: Issue;

  /**
   * Called when the user submits a new message.
   */
  onPostMessage: (message: NewMessage) => Promise<void>;

  /**
   * Optional callback invoked when an @mention chip is tapped. The host can
   * open a user profile or insert a mention into another field.
   */
  onMention?: (userId: string) => void;

  /**
   * Slot for the composer's media picker component. The host supplies the
   * correct picker for web vs. native (e.g. the app-level MediaPicker import).
   */
  mediaPicker: MediaPickerComponent;

  /**
   * Optional slot rendered above the message list. Use MessageThreadHeader as
   * a layout wrapper and supply Issue-specific affordances (severity dropdown,
   * assignee, status chips) from the consumer.
   *
   * If omitted, no header is rendered.
   */
  header?: ReactNode;

  /**
   * Optional slot rendered above the message list and below the header. Useful
   * for status pills, banners, or campaign-specific metadata that isn't part of
   * the header chrome.
   */
  statusPill?: ReactNode;

  /**
   * Placeholder text for the compose input. Forwarded to MessageThreadComposer.
   * Defaults to the IssueFooter default ("Message").
   */
  placeholder?: string;

  /**
   * Accepted media types for the attachment picker. Defaults to
   * Image + Document + Video.
   */
  acceptedMediaTypes?: MediaType[];

  /**
   * Workspace ID forwarded to the media picker for upload scoping.
   */
  workspaceId?: string;

  /**
   * Picker mode. Defaults to ATTACHMENT.
   */
  pickerMode?: PickerMode;

  /**
   * URL base for media assets, forwarded to IssueMessages.
   */
  mediaUrl: string;

  /**
   * Unread inbox items for the current issue, forwarded to IssueMessages for
   * highlighting unread messages.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inbox: any[];

  /**
   * Callback forwarded to IssueMessages to mark messages as read.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setAsRead: (
    issueId: string,
    inboxes: any[],
    invalidate: boolean
  ) => Promise<void>;

  /**
   * Optional change counter for IssueMessages that triggers a refetch when
   * updated.
   */
  tsUpdate?: string;

  /**
   * Whether to show the composer footer. Useful for read-only views (e.g.
   * campaigns sent to an OEM asset that has no reply path). Defaults to true.
   */
  showComposer?: boolean;

  /**
   * Optional callback fired when the total message count changes.
   */
  onMessagesCountChange?: (count: number) => void;

  /**
   * Forwarded to IssueMessages: enables the per-message overflow "Call" action
   * for the message's author. Gated upstream by `call.create`.
   */
  onCall?: (actor: { id: string; name: string }) => void;

  /**
   * Side Chat (plan 0022): forwarded to IssueMessages — enables the per-message
   * "Side chat" overflow action + "X replies · Side Chat" pill. Gated upstream
   * by `COMMUNICATION_PLATFORM` + `issuemessage.create`.
   */
  onOpenSideChat?: (message: Message) => void;

  /**
   * Side Chat (plan 0022): per-origin summaries (from `useSideChatSummaries`)
   * keyed by message id, forwarded to IssueMessages to drive each pill.
   */
  sideChatSummaries?: Map<string, { exists: boolean; replyCount: number }>;

  /**
   * End-to-end calling demo (plan 0021 addendum): forwarded to IssueMessages
   * — enables the "Call in progress · Join" pill on the first message. Gated
   * upstream by `COMMUNICATION_PLATFORM` + `call.create`.
   */
  callInProgress?: boolean;

  /** Opens/joins the Activity's group call. Paired with `callInProgress`. */
  onJoinCall?: () => void;

  /**
   * Anonymous share-link viewer: makes each "Anonymous (…'s Guest)" author
   * label tappable so the viewer can sign up and claim those messages.
   * Forwarded to IssueMessages; undefined for every signed-in viewer.
   */
  onAnonymousAuthorPress?: () => void;
};

/**
 * MessageThread
 *
 * A shared-ui primitive that renders a conversation thread: an optional header
 * slot, an optional status-pill slot, a scrollable message list (IssueMessages),
 * and a compose footer (MessageThreadComposer).
 *
 * Extraction scope (tight, per spec risk note):
 *   - header slot (composable — consumer provides Issue-specific chrome)
 *   - statusPill slot (optional)
 *   - IssueMessages list (reused, not reimplemented)
 *   - MessageThreadComposer (thin wrapper around IssueFooter)
 *
 * Issue-specific affordances left in the consumer:
 *   - Severity / type dropdown
 *   - Assignee chip
 *   - Status chips
 *   - Print / delete / menu actions
 *   - onboarding side effects
 *   - inbox read-marking side effects
 *
 * The `ref` forwarded here is IssueMessagesRef so callers can imperatively
 * inject optimistic messages (same pattern as issue.tsx).
 */
export const MessageThread = forwardRef<IssueMessagesRef, MessageThreadProps>(
  (props, ref) => {
    const showComposer = props.showComposer !== false;

    return (
      <View style={[styles.container, styles.flex1]}>
        {props.header}
        {props.statusPill && (
          <View style={styles.statusPillContainer}>{props.statusPill}</View>
        )}
        <View style={[styles.issueContainer, styles.flex1]}>
          <View style={styles.messages}>
            <IssueMessages
              ref={ref}
              issue={props.issue}
              mediaUrl={props.mediaUrl}
              inbox={props.inbox}
              tsUpdate={props.tsUpdate}
              setAsRead={props.setAsRead}
              onMessagesCountChange={props.onMessagesCountChange}
              onCall={props.onCall}
              onOpenSideChat={props.onOpenSideChat}
              sideChatSummaries={props.sideChatSummaries}
              callInProgress={props.callInProgress}
              onJoinCall={props.onJoinCall}
              onAnonymousAuthorPress={props.onAnonymousAuthorPress}
            />
          </View>
          {showComposer && (
            <MessageThreadComposer
              onPostMessage={props.onPostMessage}
              mediaPicker={props.mediaPicker}
              acceptedMediaTypes={props.acceptedMediaTypes}
              workspaceId={props.workspaceId}
              issueId={props.issue.id}
              mode={props.pickerMode}
              placeholder={props.placeholder}
            />
          )}
        </View>
      </View>
    );
  }
);

const styles = themedStyleSheet((role) => ({
  container: {
    width: '100%',
  },
  flex1: {
    flex: 1,
    minHeight: 0,
  },
  issueContainer: {
    ...Layout.container,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: role.canvas,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  messages: {
    flex: 1,
    minHeight: 0,
  },
  statusPillContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
}));
